import { useEffect } from 'react';

import {
  AddContextualData,
  ContextualData,
  globalObservability,
  initObservability, // eslint-disable-next-line no-restricted-imports -- File defines overrides
  useObservability, // eslint-disable-next-line no-restricted-imports -- File defines overrides
  UseObservabilityFunctions,
} from '@opendoor/observability';

import type { EventWrapper as CakeJourneyEventWrapper } from '__generated__/protobuf/journey/data/event_wrapper';
import { EventWrapper as journeyEventHelper } from '__generated__/protobuf/journey/data/event_wrapper';

import { CakeTrackingActions } from 'shared/declarations/CakeTrackingActions';

import { OD_TEAM_BOUNDARY_ERROR } from 'platform/ODTeamBoundary';

const DATADOG_RUM_APPLICATION_ID = 'c06eb858-41eb-414c-b089-76a62b00446a';
const DATADOG_RUM_APPLICATION_CLIENT_TOKEN = 'pub7d2c82e9a608fa8a014efb03023a7419';

// https://sentry.io/settings/opendoor/projects/cake/keys/
const SENTRY_DSN = 'https://95cf64e8bcd048e68d8d590b4a7d8e38@o117144.ingest.sentry.io/1535352';

type CakeTrackingCategory = 'cake';

type CakeObservability = UseObservabilityFunctions<
  CakeTrackingCategory,
  CakeTrackingActions,
  CakeJourneyEventWrapper
>;

/**
 *  Load the observability library for event tracking
 */
const loadObservability = () => {
  initObservability<CakeTrackingCategory, CakeTrackingActions, CakeJourneyEventWrapper>({
    debug: window.ENV.DEPLOY_ENV === 'development' ? true : false,
    eventTrackingCategory: 'cake',
    appName: 'cake',
    version: typeof window !== 'undefined' && window.ENV ? window.ENV.REACT_APP_GIT_SHA : 'unknown',
    sentry: {
      dsn: SENTRY_DSN,
      ignoreErrors: [
        // Error generated by a bug in auto-fill library from Chrome Mobile on iOS
        // https://github.com/getsentry/sentry/issues/5267
        /Blocked a frame with origin/,
        // This error happens when user declined the usage of cookies or sets "Block sites from setting any data" to true.
        // https://www.chromium.org/for-testers/bug-reporting-guidelines/uncaught-securityerror-failed-to-read-the-localstorage-property-from-window-access-is-denied-for-this-document
        /Failed to read the 'localStorage' property from 'Window'/,
        // This error is raised on purpose so ODTeamBoundary components can let higher-up ErrorBoundary components be responsible for rendering fallbacks.
        new RegExp(OD_TEAM_BOUNDARY_ERROR),
      ],
    },
    datadog: {
      service: 'cake',
      applicationId: DATADOG_RUM_APPLICATION_ID,
      clientToken: DATADOG_RUM_APPLICATION_CLIENT_TOKEN,
      trackInteractions: true,
      trackSessionAcrossSubdomains: true,
      // Please be careful when adding tracing origins.
      // If a server is not prepared for the new Datadog headers, then it may fail with CORS errors.
      // Relevant postmortem: https://docs.google.com/document/d/1O9eWUdXZJOS4Oha2v_dtPfjO34IU3KEDpNqjk3AtXdk/edit
      allowedTracingOrigins: [
        'https://consumer.opendoor.com',
        'https://consumer.managed.services.opendoor.com',
        'https://consumer.simplersell.com',
        'https://consumer-staging.managed.services.opendoor.com',
        'https://cake-staging.managed.services.opendoor.com',
        'https://cake.managed.services.opendoor.com',
        'https://cake-staging.simplersell.com',
        'https://cake.opendoor.com',
      ],
    },
    journeyEventHelpers: {
      toJSON: journeyEventHelper.toJSON,
    },
    journeyEndpoint: '/_journey/api/v1',
  });
};

export const useLoadObservability = () => {
  useEffect(() => {
    if (globalObservability.isInitialized()) {
      return;
    }

    loadObservability();
  }, []);
};

/**
 * Global trackEvent and trackError functions that can be used outside of react components
 */
export const globalTrackEvent: ReturnType<typeof useObservability>['trackEvent'] = (
  action,
  label,
  contextualData,
  options,
) => {
  if (globalObservability.isInitialized()) {
    globalObservability.trackEvent(
      action,
      label,
      contextualData as Partial<ContextualData>,
      options,
    );
  }
};

/**
 * Global trackError function that can be used outside of react components
 */
export const globalTrackError: ReturnType<typeof useObservability>['trackError'] = (
  message,
  error,
  extras,
) => {
  if (globalObservability.isInitialized()) {
    globalObservability.trackEvent('error', message, { data: { ...extras, error } });

    const datadogClient = globalObservability.getDatadogRumClient();
    if (datadogClient?.addError) {
      datadogClient.addError(error, { ...extras, message });
    }
  }
};

/**
 * Gives you access to Observability methods, with ContextualData from the context automatically added.
 */
const useObservabilityCake = () => {
  // Only loads observability if it hasn't been loaded yet
  useLoadObservability();

  return useObservability<CakeTrackingCategory, CakeTrackingActions, CakeJourneyEventWrapper>();
};

export { AddContextualData, loadObservability, useObservabilityCake as useObservability };
export type { CakeObservability as Observability };
